/**
 * QR Code Generation API
 *
 * Endpoint: POST /api/qr/generate
 * Content-Type: application/json
 * Response: image/png blob
 *
 * Request payload:
 * {
 *   portalId: 145667942,
 *   url: "https://example.com",
 *   margin: 16,
 *   size: 600,
 *   color: "#0f3460",           // foreground color
 *   errorCorrection: "High",    // Low | Medium | Quartile | High
 *   roundBlockSizeMode: "Margin", // Margin | Enlarge | Shrink | None
 *   labelText: "My Label",      // optional
 *   labelSize: 12,              // optional
 *   labelAlign: "Center",       // optional — Left | Center | Right
 *   labelColor: "#ff0000"       // optional
 * }
 */
import apiClient from './axiosInstance';

/**
 * Capitalizes the first letter and lowercases the rest.
 * Used to convert state values (e.g. "high") to API values (e.g. "High").
 *
 * @param {string} str
 * @returns {string}
 */
const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;

/**
 * Normalizes any CSS color string to a 6-digit hex string (#rrggbb).
 * Handles:
 *  - 6-digit hex          : "#ae2727"       → "#ae2727"
 *  - 8-digit hex (+ alpha): "#ae272780"     → "#ae2727"
 *  - rgb()                : "rgb(174,39,39)"  → "#ae2727"
 *  - rgba()               : "rgba(174,39,39,0.5)" → "#ae2727"
 *
 * @param {string} color
 * @returns {string} lowercase 6-digit hex, e.g. "#ae2727"
 */
const toHex6 = (color) => {
  if (!color) return color;

  const s = color.trim();

  // Already 6-digit hex
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();

  // 8-digit hex (strip alpha)
  if (/^#[0-9a-f]{8}$/i.test(s)) return s.slice(0, 7).toLowerCase();

  // 3-digit hex → expand
  if (/^#[0-9a-f]{3}$/i.test(s)) {
    return '#' + [...s.slice(1)].map((c) => c + c).join('');
  }

  // rgb() / rgba()
  const m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) {
    return (
      '#' +
      [m[1], m[2], m[3]]
        .map((n) => parseInt(n, 10).toString(16).padStart(2, '0'))
        .join('')
    );
  }

  // Fallback — return as-is
  return s;
};

/**
 * Sends a QR code generation request and returns a local blob URL for display.
 *
 * Field name mapping from internal state → API:
 *  foregroundColor       → color
 *  backgroundColor       → background
 *  errorCorrectionLevel  → errorCorrection  (capitalized)
 *  roundBlockSizeMode    → roundBlockSizeMode (capitalized)
 *  labelFontSize         → labelSize
 *  labelAlignment        → labelAlign (capitalized)
 *  utmSource,utmMedium,utmCampaign,utmContent → nested utmParams object
 *
 * @param {{
 *   portalId: number,
 *   url: string,
 *   size: number,
 *   margin: number,
 *   errorCorrectionLevel: string,
 *   roundBlockSizeMode: string,
 *   foregroundColor: string,
 *   backgroundColor: string,
 *   logoId?: number,
 *   logoResizeToWidth?: number,
 *   labelText?: string,
 *   labelAlignment?: string,
 *   labelFontSize?: number,
 *   labelColor?: string,
 *   utmSource?: string,
 *   utmMedium?: string,
 *   utmCampaign?: string,
 *   utmContent?: string,
 *   utmTerm?: string,
 * }} params
 * @returns {Promise<{ qrImageUrl: string }>} Object URL pointing to the PNG blob
 * @throws {Error} normalized error with `.message` and `.status`
 */
export const generateQRCode = async ({
  url,
  size,
  margin,
  errorCorrectionLevel,
  roundBlockSizeMode,
  foregroundColor,
  backgroundColor,
  logoId,
  logoResizeToWidth,
  labelText,
  labelAlignment,
  labelFontSize,
  labelColor,
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
}) => {
  const payload = {
    url: url.trim().replace(/\/$/, ''),
    size,
    margin,
    color: toHex6(foregroundColor),
    background: toHex6(backgroundColor),
    errorCorrection: capitalize(errorCorrectionLevel),
    roundBlockSizeMode: capitalize(roundBlockSizeMode),
  };

  // Logo — only include when an uploaded logo ID is available
  if (logoId != null) {
    payload.logo = {
      id: logoId,
      resizeWidth: logoResizeToWidth,
    };
  }

  // Label — nested object, only when the user has typed a label
  if (true) {
    payload.label = {
      text: labelText.trim(),
      align: capitalize(labelAlignment),
      size: labelFontSize,
      color: toHex6(labelColor),
    };
  }

  // UTM params — only when at least one field is filled; keys use utm_ prefix
  if (utmSource?.trim() || utmMedium?.trim() || utmCampaign?.trim() || utmContent?.trim() || utmTerm?.trim()) {
    payload.utmParams = {};
    if (utmSource?.trim())   payload.utmParams.utm_source   = utmSource.trim();
    if (utmMedium?.trim())   payload.utmParams.utm_medium   = utmMedium.trim();
    if (utmCampaign?.trim()) payload.utmParams.utm_campaign = utmCampaign.trim();
    if (utmContent?.trim())  payload.utmParams.utm_content  = utmContent.trim();
    if (utmTerm?.trim())     payload.utmParams.utm_term     = utmTerm.trim();
  }

  const response = await apiClient.post('/qr/generate', payload);

  // The API returns JSON: { status, message, data: { mimeType, dataUri } }
  const dataUri = response.data?.data?.dataUri;
  if (!dataUri) throw new Error('Invalid response: missing dataUri');

  return { qrImageUrl: dataUri };
};

/**
 * Fetches the paginated list of QR codes for a given portal.
 *
 * GET /api/qr.php/list?portalId=<id>&page=<n>&limit=<n>
 *
 * Response shape:
 * {
 *   status: "success",
 *   message: string,
 *   data: Array<{ id, slug, url, created_at, title, size, margin,
 *                  color, background, labelText, labelSize, labelAlign,
 *                  labelColor, logoEnabled, logoUploadId, logoResizeWidth,
 *                  errorCorrection, roundBlockSizeMode, utmParams: string }>,
 *   pagination: { page, limit, offset, total, pages, hasNext, hasPrev }
 * }
 *
 * @param {{ portalId: number, page?: number, limit?: number }}
 * @returns {Promise<{ list: Array, pagination: object }>}
 * @throws {Error}
 */
export const fetchQRCodeList = async ({ portalId, page = 1, limit = 10 }) => {
  const response = await apiClient.get(`${process.env.REACT_APP_BASE_URL}/qr/list`, {
    params: { portalId, page, limit },
  });

  const { status, message, data, pagination } = response.data;

  if (status !== 'success') {
    throw new Error(message || 'Failed to fetch QR code list.');
  }

  // Parse utmParams string → object for convenience
  const list = (data || []).map((item) => {
    let utmParams = {};
    try {
      utmParams = JSON.parse(item.utmParams || '{}');
    } catch {
      utmParams = {};
    }
    return { ...item, utmParams };
  });

  return { list, pagination: pagination || {} };
};
